// @flow

import type { SerialsObjectType, TagType, TracksObjectType } from '../flowTypes'
import { getLibrary, getPopularTacks } from '../services/dipseaApi'
import { cloneDeep } from 'lodash'
import type { TrackPropsType } from './Track'
import { convertTrackToProps, reducePopularity } from './Track'
import { sortSlugs } from './helpers'

export async function getPrimaryTagsForSiteMap (): Promise<string[]> {
  const library = await getLibrary()
  const tracks = library.tracks
  const tags = []
  Object.keys(tracks || {}).forEach((trackId: string): void => {
    const track = tracks[trackId]
    const tagIds = Object.keys(track.tag_ids || {})
    tagIds.forEach((tagId: string): void => {
      if (!tags.includes(tagId) && !filterTag(tagId)) {
        tags.push(tagId)
      }
    })
  })

  return Object.keys(library?.slugs?.tags).filter((slug: string): boolean => tags.includes(library?.slugs?.tags[slug]))
}

export async function getTagSlugsAsync (): Promise<string[]> {
  const library = await getLibrary()
  return Object.keys(library?.slugs?.tags)
}

export type TagWithTracksType = {
  ...TagType,
  tracks: TrackPropsType[]
}

export function filterSeriesWithRepeatingTracks (tracks: TracksObjectType, series: SerialsObjectType, trackIds: string[]): string[] {
  const seriesToFilter = Object.keys(series || {}).filter((seriesId: string): boolean => !!series[seriesId].seriesHasRepeatImages)

  if (seriesToFilter.length) {
    let tracksToFilter: string[] = []
    seriesToFilter.forEach((seriesId: string): void => {
      const serial = series[seriesId]
      let sortedTracks = Object.keys(serial.track_ids).sort((a: string, b: string): number => serial.track_ids[a].index - serial.track_ids[b].index)
      sortedTracks = sortedTracks.slice(1, sortedTracks.length)
      // filter out any +you
      sortedTracks = sortedTracks.filter((trackId: string): boolean => !Object.keys(tracks[trackId].tag_ids || {}).includes('-Lzjv_nyDUi2lRy47uXe'))
      tracksToFilter = tracksToFilter.concat(sortedTracks)
    })
    return trackIds.filter((trackId: string): boolean => !tracksToFilter.includes(trackId))
  }

  return trackIds
}

export async function getTagBySlugAsync (slug: string): Promise<TagType> {
  const [library] = await Promise.all([getLibrary()])
  const tagId = library?.slugs?.tags[slug]
  const tag = cloneDeep(library?.tags[tagId] || {})
  return {
    ...tag,
    id: tagId
  }
}

export async function getTagTracksAsync (tag: TagType): Promise<TrackPropsType[]> {
  const [library, popularity] = await Promise.all([getLibrary(), getPopularTacks()])
  const tracks = library?.tagsToTracks[tag.id]?.track_ids || {}
  const popularityMap = reducePopularity(popularity, [])
  const trackIds = filterSeriesWithRepeatingTracks(library.tracks, library.series, Object.keys(tracks))
  return trackIds
    .filter((trackId: string): boolean => !!library?.tracks[trackId])
    .sort((a: string, b: string): number => tracks[a] - tracks[b])
    .map((trackId: string, index: number): TrackPropsType => {
      // $FlowFixMe
      return convertTrackToProps({
        ...library?.tracks[trackId],
        id: trackId
      }, library, popularityMap)
    })
    .filter((track: TrackPropsType | null): boolean => !!track)
}

export async function getTagSlug (id: string): Promise<string> {
  const library = await getLibrary()
  const tag = library?.tags[id]
  return '/tags/' + sortSlugs(tag.slugs)[0]
}

export type TagInfoType = { id: string, slug: string, title: string, imageUrl: string | null, description: string | null, logLine: string | null }

const popularTags = ['-LKOR523Akw7BJ8BgLfx', '-LKXUXjLdIdvttz-BcZS', '-LKXrBC5Hfx9pvWJV01Q', '-LLBybkFSC1uOcvpNvTZ', '-LM4S5QNygw3PQGtg7d5', '-LM4UkaV6S-16_uOYRvZ', '-LM4UoWuXJrJ17MoIgbQ', '-LM4Us349qMmBaZzd75S', '-LM4VEuXid4sF2mSSgEC', '-LM4VFzSPck4e006jmib', '-LM4VQr2brRroH8_Nn4S', '-LM4XJBFQYT1voBOMNOI', '-LM4XbeCuctQSGu57Zog', '-LM4YnmrJZEoU9aMN9SV', '-LM4YoskFaCqBvwqqF2z', '-LM4ZVJEcKubo8AZZExy', '-LM4Zw1l7dxX6fGhNCNp', '-LM4aTlo27vB4_hjMv5J', '-LMFFJmlCbvGKLVCODi9', '-LMFFXTXSkdYvP8zMCg1', '-LMGCMe2NSqn4BfN_B5q', '-LMJd6DMJBbAtdBHLB6m', '-LMJq1icOCd57zrqaT8n', '-LMJqiRbdLYJAb_W1kvo', '-LMJrXzYoJhnlSO8_wVO', '-LMK5HFJao2XzMwBdyND', '-LMdScYcXEs_nyAwSP3e', '-LO5m0Uwb3CGOApDR0SY', '-LSwwGox6ZDyPYXp1-tb', '-LUHH5pAYi4xgWENoj6U', '-LUHIXL6_2rszFELhUAk', '-LXFi_Y0hPdiWUqu0Ylk', '-LdpUMPvHZo4REXBzLSN', '-LfL0v4ETKmgA4eGQ-Ax', '-Lgwx8kbmm7n2jSIgTjg', '-LhWAIz3PRhrPAWrLDY0', '-Lkz_LyQTbHuZOxhZ_DV', '-LkzarvAjaeuBCfESzZH', '-Lkzch_rXnMsCExsffjp', '-LlSUbTFBgpYISEYX0fs', '-Lm0S8uZhpxkv0ODIT0y', '-LmfPVqyOk_Lvyz1Sw0T', '-Lmg0IQEN6tv7P2eU6Qa', '-Lngt8Cfx0NiYux_5gcI', '-LoplqBayipMCk7KvLoB', '-LpxpUpUfG4SVy9o9sZi', '-LqIYNyCDbLRXAeNqG00', '-Lr5-RSlrxYWKXQm-v8o', '-LrGWMNhw4jFzPZ6FfSy', '-Lyyov6JvYWzdB2GnKHj', '-LyyqFM84R6JA-GWS-1X', '-Lzjv_nyDUi2lRy47uXe', '-M0n1CMD55Kw4z4G_l0C', '-M7ObVJ8FrMMOiVpYJRP', '-MAbnIzGcMT4GezYZB5S', '-MDauINfhymF7CB3mjkN', '-MGk9my_E9Ohlu0oxFhL', '-MLjHdTNwuM6TzVLH8oQ', '-MSnnzwIaMUM3m3Xz6ob', '-MZZ9jc5UvqR1gCNkAhi', '-MZZHYtfkBUJQPVHSl_e', '-MZZpAaul7wzNkjYqSFP', '-MZZtTO89i4XTEHwWUM0', '-McpbdrfpwJSTkQz-jOB', '-Mm__0at6B0sdlv-NY2y', '-MngWHveaWKeFy_XYkQd', '-MoAUr7DVkDARV7agEsg', '-MoAUu5T7CdmtxPW0YEH', '-MoAWEfclxTfqI3sIKOe', '-MoAWFnUsNBM8DxOg3uC', '-N1e18GUtojQdCxSwY5R', '-N1e2H0J2JWUh-FcAKEt', '-N92uf_5tznR2rbIYMVT', '-NLgy1Uzw7UO42798zKl', '-NM1En0BmA63MGtsaY4k', '-NMjZJs4ZUAbNLyoojEk', '-NMjZLSRzDU7RKu2QCE5', '-NMjZOP8E2ME8dGsaU3K', '-NMjZjOGS2hZYVl1RoMG', '-NSlbbypFmI44xyxtXaA', '-NSleB1mWk3ZD-nBGBtX', '-NToUsIL2piO43zzT9J0', '-NW306BnWRYhN6ysEfZe', '-NW3DXB5HKlNl301blci', '-NYYq4-6s03wzv8OtZTl', '-NYds4DwIRC-O3HKChur', '-NYds9G5jE-Ay_2GLYuf', '-NYdsPwT13x-KIED3Ik0', '-N_fhe9q0OXrK4Df-RUM', '-N_fhmwJ0DlgGPI7KpGZ', '-N_fhqR-kT-cb0D7Du6S', '-N_nkCiy6bMRqgBf_QTP', '-Nbq-cAnEuOdqp1uVZ4s', '-NdpLXcxQdqn71M8Rpgx', '-NdpLa9w5gUU6FkEDtZj', '-NeiTDK_l0AoJBbJOJIl', '-NeiUrzQ5BvFzOfeIcjf', '-NeiY21zKBb7K-0T-LOn', '-NqTEdC4wRj6N6uwAQrj', '-NsiLK0wkOyRSgnCdC3M']

export async function getPopularTagsAysnc (): Promise<TagInfoType[]> {
  const library = await getLibrary()
  return popularTags.filter((tagId: string): boolean => !filterTag(tagId) && !!library?.tags[tagId]).map((id: string): TagInfoType => {
    const tag = library?.tags[id]
    return {
      id,
      slug: '/tags/' + sortSlugs(tag.slugs)[0],
      title: tag.name,
      imageUrl: tag.image_url || null,
      logLine: null,
      description: null
    }
  })
}

const tagsToHide = [
  '-LM4XeUc7efmiqbypIF5',
  '-MZZ9jc5UvqR1gCNkAhi',
  '-NsiLK0wkOyRSgnCdC3M',
  '-LhWAIz3PRhrPAWrLDY0',
  '-M0n1CMD55Kw4z4G_l0C',
  '-N1e18GUtojQdCxSwY5R',
  '-LfL0v4ETKmgA4eGQ-Ax',
  '-LO5m0Uwb3CGOApDR0SY',
  '-LUHIXL6_2rszFELhUAk',
  '-LMJrXzYoJhnlSO8_wVO',
  '-Mm__0at6B0sdlv-NY2y',
  '-LMGCMe2NSqn4BfN_B5q',
  '-LlSUbTFBgpYISEYX0fs',
  '-LMGC5obzp08jnJs9u24',
  '-MoAUv_b3kupu71lI6RN',
  '-LQew_M6QhPreo4i7k2P',
  '-LUHH5pAYi4xgWENoj6U',
  '-LUHIRE2YP3EM3ddZgy4',
  '-LqMcRrb1LjNM4RGaNHf',
  '-MoAUu5T7CdmtxPW0YEH',
  '-MoAV4uEmIwRceonnbjA',
  '-MoAUyjaZ0EKU8I_nrRv',
  '-LM4XeUc7efmiqbypIF5',
  '-LqNop5WjvLVmM2llAnZ',
  '-MoAVBoxk3suVQwEbwZv',
  '-MoAWEfclxTfqI3sIKOe',
  '-MoAVA-juqGk2WFngYzq',
  '-MoAWFnUsNBM8DxOg3uC',
  '-LMJq1icOCd57zrqaT8n',
  '-NM1FWaUuA6KH5j6jY_g'
]

export function filterTag (tagId?: string): boolean {
  if (!tagId) {
    return false
  }
  return tagsToHide.includes(tagId)
}
